import { ReceiptNode } from "../../../build/types";
import { Fragment } from "react";
import { format, isValid, parseISO } from "date-fns";
import { ReceiptLabel } from "../../../build/constants";
import { ReceiptData } from "../../../build/printLayoutTypes";
import "./style.scss";
import React from "react";

const getValidDate = (rawDate: any) => {
    if (!rawDate) return "";

    try {
        const parsedDate = typeof rawDate === "string" ? parseISO(rawDate) : rawDate;

        if (!isValid(parsedDate)) return "Invalid date";

        return format(parsedDate, "dd MMMM yyyy");
    } catch (error) {
        return "";
    }
};

interface ReceiptContainerProps {
    node: ReceiptNode;
    receiptData: ReceiptData | null;
    receiptLabels: ReceiptLabel;
    isGift: boolean;
    formatCurrency: (amount?: number) => string;
    formatAmount: (amount?: number) => string;
}

export const ProductDetails = ({
    node,
    receiptData,
    receiptLabels,
    isGift,
    formatCurrency,
    formatAmount
}: ReceiptContainerProps) => {
    return (
        <>
            {receiptData?.products ? (
                receiptData.products?.map((product, productIndex) => (
                    <div className="product" key={`product-${productIndex}`}>
                        <div className="product-wrapper y-pad-small normal">
                            <div className="bold" style={node.styles?.productTitle}>{`${
                                !node.settings.showValues || isGift ? `${product.quantity} x ` : ""
                            } ${product.name}`}</div>
                            <div className="product-price-wrapper">
                                {!node.settings.showValues || isGift ? null : (
                                    <>
                                        {product.quantity && product.quantity > 1 ? (
                                            <div className="multiple-product">
                                                ({product.quantity} x {formatCurrency(product.price)})
                                            </div>
                                        ) : null}
                                        <div style={node.styles ? { ...node.styles.productPrice } : { fontWeight: 300 }}>
                                            {formatCurrency(product.totalPrice)}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {product.sku && node.settings.showSku ? (
                            <div className="sku" style={node.styles?.sku}>
                                SKU: {product.sku}
                            </div>
                        ) : null}

                        {product.description && node.settings.showDescription ? (
                            <div className="item-info y-pad-small" style={node.styles?.productDescription}>
                                {product.description}
                            </div>
                        ) : null}

                        {product.modifiers &&
                            node.settings.showVariations &&
                            product.modifiers.length > 0 &&
                            product.modifiers.map((modifier, modifierIndex) => (
                                <div className="modifiers" key={`modifier-${modifierIndex}`}>
                                    <div className="modifiers-wrapper" style={node.styles?.productVariation}>
                                        <div>+ {modifier.name}</div>
                                        {!node.settings.showValues || isGift ? "" : <div>+ {formatAmount(modifier.amount)}</div>}
                                    </div>
                                </div>
                            ))}
                        {node.settings.showNotes ? (
                            <div className="y-pad-small">{product.productNote ? product.productNote : ""}</div>
                        ) : null}
                        <div className="item-discount-container y-pad-small">
                            {product.discount &&
                            product.discount.amount > 0 &&
                            node.settings.showValues &&
                            !isGift &&
                            node.settings.showProductDiscount ? (
                                <div className="item-discount-wrapper" style={node.styles?.productDiscount}>
                                    <div>
                                        {receiptLabels.itemDiscount ? receiptLabels.itemDiscount : ""}
                                        {node.settings.showProductDiscount ? `(${product.discount.percentAmount})` : ""}
                                    </div>
                                    <div>-{formatCurrency(product.discount.amount)}</div>
                                </div>
                            ) : null}
                            {product.cartDiscount &&
                            product.cartDiscount.amount > 0 &&
                            node.settings.showValues &&
                            !isGift &&
                            node.settings.showProductDiscount ? (
                                <div className="item-discount-wrapper" style={node.styles?.productDiscount}>
                                    <div>
                                        {receiptLabels.cartDiscount ? receiptLabels.cartDiscount : ""}
                                        {node.settings.showProductDiscount ? `(${product.cartDiscount.percentAmount})` : ""}
                                    </div>
                                    <div>-{formatCurrency(product.cartDiscount.amount)}</div>
                                </div>
                            ) : null}
                        </div>

                        {
                            <div className="item-fee-container y-pad-small">
                                {product.fee?.itemFee &&
                                product.fee.itemFee.amount > 0 &&
                                node.settings.showValues &&
                                !isGift &&
                                node.settings.showProductFee ? (
                                    <div className="item-fee-wrapper" style={node.styles?.productDiscount}>
                                        <div>
                                            {"Item Fee"}
                                            {`(${product.fee.itemFee.percentAmount})`}
                                        </div>
                                        <div>+{formatCurrency(product.fee.itemFee.amount)}</div>
                                    </div>
                                ) : null}
                                {product.fee?.cartFee &&
                                product.fee.cartFee.amount > 0 &&
                                node.settings.showValues &&
                                !isGift &&
                                node.settings.showProductFee ? (
                                    <div className="item-fee-wrapper" style={node.styles?.productDiscount}>
                                        <div>
                                            {"Cart Fee"}
                                            {`(${product.fee.cartFee.percentAmount})`}
                                        </div>
                                        <div>+{formatCurrency(product.fee.cartFee.amount)}</div>
                                    </div>
                                ) : null}
                            </div>
                        }
                        {node.settings.showValues && !isGift && node.settings.showProductTaxes ? (
                            <hr className="divider" />
                        ) : null}

                        <div className="product-tax-container">
                            {product.taxes &&
                                node.settings.showValues &&
                                !isGift &&
                                node.settings.showProductTaxes &&
                                product.taxes.length > 0 &&
                                product.taxes.map((tax, taxIndex) =>
                                    tax.amount > 0 ? (
                                        <div
                                            className="product-tax-wrapper"
                                            style={node.styles?.productTax}
                                            key={`tax-${taxIndex}`}
                                        >
                                            <div>
                                                {tax.name} {`(${tax.percentAmount})`}
                                            </div>
                                            <div>{formatCurrency(tax.amount)}</div>
                                        </div>
                                    ) : null
                                )}
                        </div>

                        {node.settings.showValues && !isGift && node.settings.showProductTaxes ? (
                            <hr className="divider" />
                        ) : null}

                        {!node.settings.showValues || isGift ? (
                            ""
                        ) : (
                            <div>
                                <div className="product-total">
                                    <div className="product-total-wrapper">
                                        <div style={node.styles ? { ...node.styles.productTotal } : { fontWeight: 300 }}>
                                            {receiptLabels.productTotal}
                                        </div>
                                        <div className="bold" style={node.styles?.productTotalPrice}>
                                            {formatCurrency(product.productTotal)}
                                        </div>
                                    </div>
                                </div>
                                {receiptData?.orderNotes?.length ? (
                                    <div className="item-fee-wrapper" style={node.styles?.productDiscount}>
                                        <div className="item-fee-wrapper" style={node.styles?.productDiscount}>
                                            {receiptLabels.orderNotes || "Order Notes"}
                                        </div>
                                        <div className="item-fee-wrapper" style={node.styles?.productDiscount}>
                                            {receiptData.orderNotes.map((note, index) =>
                                                note ? <div key={`note-${index}`}>{note}</div> : null
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                ))
            ) : (
                <div className="product">
                    <hr className="divider" />
                </div>
            )}
            {receiptData?.customSales && receiptData.customSales.length > 0 ? (
                receiptData.customSales.map((customSale, customSaleIndex) => (
                    <div className="product" key={`customSale-${customSaleIndex}`}>
                        <div className="product-wrapper y-pad-small normal">
                            <div className="bold" style={node.styles?.productTitle}>{`${
                                !node.settings.showValues || isGift ? `${customSale.quantity} x ` : ""
                            } ${customSale.name}`}</div>
                            <div className="product-price-wrapper">
                                {!node.settings.showValues || isGift ? null : (
                                    <>
                                        {customSale.quantity && customSale.quantity > 1 ? (
                                            <div className="multiple-product">
                                                ({customSale.quantity} x {formatCurrency(customSale.price)})
                                            </div>
                                        ) : null}
                                        <div style={node.styles ? { ...node.styles.productPrice } : { fontWeight: 300 }}>
                                            {formatCurrency(customSale.totalPrice)}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {node.settings.showNotes ? (
                            <div className="y-pad-small">{customSale.productNote ? customSale.productNote : ""}</div>
                        ) : null}
                        <div className="item-discount-container y-pad-small">
                            {customSale.cartDiscount &&
                            customSale.cartDiscount.amount > 0 &&
                            node.settings.showValues &&
                            !isGift &&
                            node.settings.showProductDiscount ? (
                                <div className="item-discount-wrapper" style={node.styles?.productDiscount}>
                                    <div>
                                        {receiptLabels.cartDiscount ? receiptLabels.cartDiscount : ""}
                                        {node.settings.showProductDiscount ? `(${customSale.cartDiscount.percentAmount})` : ""}
                                    </div>
                                    <div>-{formatCurrency(customSale.cartDiscount.amount)}</div>
                                </div>
                            ) : null}
                        </div>

                        {
                            <div className="item-fee-container y-pad-small">
                                {customSale.fee?.cartFee &&
                                customSale.fee.cartFee.amount > 0 &&
                                node.settings.showValues &&
                                !isGift &&
                                node.settings.showProductFee ? (
                                    <div className="item-fee-wrapper" style={node.styles?.productDiscount}>
                                        <div>
                                            {"Cart Fee"}
                                            {`(${customSale.fee.cartFee.percentAmount})`}
                                        </div>
                                        <div>+{formatCurrency(customSale.fee.cartFee.amount)}</div>
                                    </div>
                                ) : null}
                            </div>
                        }
                        {node.settings.showValues && !isGift && node.settings.showProductTaxes ? (
                            <hr className="divider" />
                        ) : null}

                        <div className="product-tax-container">
                            {customSale.taxes &&
                                node.settings.showValues &&
                                !isGift &&
                                node.settings.showProductTaxes &&
                                customSale.taxes.length > 0 &&
                                customSale.taxes.map((tax, taxIndex) =>
                                    tax.amount > 0 ? (
                                        <div
                                            className="product-tax-wrapper"
                                            style={node.styles?.productTax}
                                            key={`tax-${taxIndex}`}
                                        >
                                            <div>
                                                {tax.name} {`(${tax.percentAmount})`}
                                            </div>
                                            <div>{formatCurrency(tax.amount)}</div>
                                        </div>
                                    ) : null
                                )}
                        </div>

                        {node.settings.showValues && !isGift && node.settings.showProductTaxes ? (
                            <hr className="divider" />
                        ) : null}

                        {!node.settings.showValues || isGift ? (
                            ""
                        ) : (
                            <div>
                                <div className="product-total">
                                    <div className="product-total-wrapper">
                                        <div style={node.styles ? { ...node.styles.productTotal } : { fontWeight: 300 }}>
                                            {receiptLabels.productTotal}
                                        </div>
                                        <div className="bold" style={node.styles?.productTotalPrice}>
                                            {formatCurrency(customSale.productTotal)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            ) : null}
        </>
    );
};

export const CartDetails = ({ node, receiptData, receiptLabels, isGift, formatCurrency }: ReceiptContainerProps) => {
    return (
        <>
            <div className="amount-container">
                <div className="amount-box" style={node.styles?.cartTotal}>
                    <div className="amount-wrapper">
                        {!node.settings.showValues || isGift ? null : (
                            <>
                                <div className="amount-label" style={node.styles?.cartTotal}>
                                    {receiptLabels.subtotal ? receiptLabels.subtotal : ""}
                                </div>
                                <div className="amount-value" style={node.styles?.cartTotal}>
                                    {formatCurrency(receiptData?.subtotal)}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="amount-wrapper">
                        {receiptData?.shippingAmount && receiptData.shippingAmount > 0 && node.settings.showValues && !isGift ? (
                            <>
                                <div className="amount-label" style={node.styles?.cartTotal}>
                                    {receiptLabels.shipping}
                                </div>
                                <div className="amount-value" style={node.styles?.cartTotal}>
                                    {formatCurrency(receiptData.shippingAmount)}
                                </div>
                            </>
                        ) : null}
                    </div>

                    {receiptData?.totalTaxes &&
                    receiptData.totalTaxes.length > 0 &&
                    node.settings.showValues &&
                    !isGift &&
                    node.settings.showCartTaxes
                        ? receiptData.totalTaxes.map((tax, taxIndex) =>
                              tax.amount > 0 ? (
                                  <div className="amount-wrapper" key={`tax-${taxIndex}`}>
                                      <div className="amount-label" style={node.styles?.cartTotal}>
                                          {tax.name}
                                          {"(" + tax.rate + ")"}
                                      </div>
                                      <div className="amount-value" style={node.styles?.cartTotal}>
                                          {formatCurrency(tax.amount)}
                                      </div>
                                  </div>
                              ) : null
                          )
                        : null}

                    {receiptData &&
                    receiptData.totalDiscount > 0 &&
                    node.settings.showValues &&
                    !isGift &&
                    node.settings.showCartDiscount ? (
                        <div className="amount-wrapper">
                            <div className="amount-label" style={node.styles?.cartTotal}>
                                {receiptLabels.totalDiscount}
                            </div>
                            <div className="amount-value" style={node.styles?.cartTotal}>
                                -{formatCurrency(receiptData?.totalDiscount)}
                            </div>
                        </div>
                    ) : null}

                    {receiptData?.cartFees && receiptData?.cartFees.length > 0 && !isGift && node.settings.showCartFee
                        ? receiptData.cartFees.map((fee, feeIndex) =>
                              fee.amount > 0 ? (
                                  <div className="amount-wrapper" key={`fee-${feeIndex}`}>
                                      <div className="amount-label" style={node.styles?.cartTotal}>
                                          {`Total Fee ${feeIndex + 1}`}
                                      </div>
                                      <div className="amount-value" style={node.styles?.cartTotal}>
                                          {formatCurrency(fee.amount)}
                                      </div>
                                  </div>
                              ) : null
                          )
                        : null}

                    {receiptData?.tips &&
                    receiptData.tips.length > 0 &&
                    node.settings.showValues &&
                    !isGift &&
                    node.settings.showTips
                        ? receiptData.tips.map((tip, tipIndex) =>
                              tip.amount > 0 ? (
                                  <div className="amount-wrapper" key={`tip-${tipIndex}`}>
                                      <div className="amount-label" style={node.styles?.cartTotal}>
                                          {tip.name}({tip.percentAmount})
                                      </div>
                                      <div className="amount-value" style={node.styles?.cartTotal}>
                                          {formatCurrency(tip.amount)}
                                      </div>
                                  </div>
                              ) : null
                          )
                        : null}

                    <div className="amount-wrapper y-pad-small">
                        {!node.settings.showValues || isGift ? null : (
                            <>
                                <div className="amount-label" style={node.styles?.cartTotal}>
                                    {receiptLabels.total}
                                </div>
                                <div className="amount-value" style={node.styles?.cartTotal}>
                                    {formatCurrency(receiptData?.total)}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {!node.settings.showValues || isGift || (receiptData && receiptData.payments.length < 1) ? null : (
                    <>
                        <hr className="divider" style={{ margin: "3px 0", padding: 0 }} />
                        <hr className="divider" style={{ margin: "3px 0", padding: 0 }} />
                    </>
                )}

                <div className="amount-emv">
                    {!node.settings.showValues || isGift ? null : (
                        <>
                            {receiptData?.payments && receiptData.payments.length > 0
                                ? receiptData.payments.map((payment, paymentIndex) =>
                                      payment.method.toLowerCase() === "card" ? (
                                          <React.Fragment key={`payment-${paymentIndex}`}>
                                              <div className="amount-wrapper" style={node.styles?.cartPayment}>
                                                  <div className="amount-label" style={node.styles?.cartPayment}>
                                                      {payment.method.charAt(0).toUpperCase() + payment.method.slice(1)} (
                                                      {getValidDate(payment.date)}
                                                  </div>
                                                  <div className="amount-value" style={node.styles?.cartPayment}>
                                                      {formatCurrency(payment.amount)}
                                                  </div>
                                              </div>
                                              {payment?.emv && payment.emv.length > 0 ? (
                                                  <div className="emv-data-container" style={node.styles?.cartEMV}>
                                                      <div className="bold">EMV Data</div>
                                                      {payment.emv.map((emv, emvIndex) => (
                                                          <div className="emv-data-wrapper" key={`emv-${emvIndex}`}>
                                                              <div>{emv.name}:</div>
                                                              <div>{emv.info}</div>
                                                          </div>
                                                      ))}
                                                  </div>
                                              ) : null}
                                          </React.Fragment>
                                      ) : null
                                  )
                                : null}

                            {receiptData?.payments && receiptData.payments.length > 0
                                ? receiptData.payments
                                      .filter(payment => payment.method.toLowerCase() !== "card")
                                      .map((payment, paymentIndex) => (
                                          <Fragment key={`payment-${paymentIndex}`}>
                                              <div className="amount-wrapper" style={node.styles?.cartPayment}>
                                                  <div className="amount-label">
                                                      {payment.method.charAt(0).toUpperCase() + payment.method.slice(1)} (
                                                      {getValidDate(payment.date)})
                                                  </div>
                                                  <div className="amount-value">{formatCurrency(payment.amount)}</div>
                                              </div>

                                              {payment.method.toLowerCase().includes("cash") && payment.change ? (
                                                  <div className="amount-wrapper">
                                                      <div className="amount-label" style={node.styles?.cartPayment}>
                                                          Change
                                                      </div>
                                                      <div className="amount-value" style={node.styles?.cartPayment}>
                                                          {formatCurrency(payment.change)}
                                                      </div>
                                                  </div>
                                              ) : null}
                                          </Fragment>
                                      ))
                                : null}
                        </>
                    )}
                </div>

                {!node.settings.showValues || isGift || (receiptData && receiptData.payments.length < 1) ? null : (
                    <>
                        <hr className="divider" style={{ margin: "3px 0", padding: 0 }} />
                        <hr className="divider" style={{ margin: "3px 0", padding: 0 }} />
                    </>
                )}
            </div>
        </>
    );
};
